// React
import { useEffect, useState } from "react";
import clsx from "clsx";

// Third Party
import moment from "moment";

// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useSalesLogChart } from "@DataPages/context/salesOperations/salesLog/SalesLogChartsContext";
import useAggregationExtended from "@/Layouts/components/Tables/DataSmartTable/aggregation/Aggregation";

// Helpers
import { accountFormat } from "@Common/helpers/number";
import { getCellClassNameFn, isFooterCell } from "@/Common/helpers/Tables";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";
import { generateSx, getRowClassName } from "@/Layouts/components/Tables/DataSmartTable/helpers";

// Service
import actions from "@Constants/actionsList";

// Helpers
import { notEqualsOperator } from "@Layouts/components/Tables/DataSmartTable/helpers/custom-filters";

// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import { TypographyCell } from "@Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import IncludeCheckBoxFilter from "./IncludeCheckBoxFilter";

// MUI
import { Stack, SxProps, Typography } from "@mui/material";
import { getGridStringOperators, GridColumnHeaderParams, GridRenderCellParams } from "@mui/x-data-grid-premium";

// Assets
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";
import empty from "@/assets/images/commons/empty.png";

// Constants
import { tableKeys } from "@/Constants/tableKeys";
import { DATE_FORMAT } from "@Constants/date";
import { BACKOUT, DEAL_TYPE_MAP } from "@/DataPages/constants/sales-operations";
import { HEADER_CLASS_NAME_2 } from "@/Constants/tables";

// Types
import { GridCellParams, GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid-pro";
import { ISalesLog } from "@DataPages/types/salesOperations/salesLog/model";
import { DictionaryMap } from "@Common/interfaces/types";
import { DataSmartTableProps } from "@/Layouts/components/types";
import { TDealTypeKey } from "@/DataPages/types/salesOperations/salesLog/utils";

const AUTO_GENERATED_ID = "auto-generated-group-footer-root";
const HEADER_CLASS_NAME = "table-header-top-aligned";

type ProductDetails = {
	product: string;
	value: number;
};

type ProductColumns = {
	products: ProductDetails[];
	prefix: string;
	visibilityModel: VisibilityModel;
	footerModelsToShow: DictionaryMap<string>;
	defaultColumnsToExpand: string[];
};

type VisibilityModel = {
	_id: boolean;
	salesEmployeeId: boolean;
	financeEmployeeId: boolean;
	tradeACV: boolean;
	tradeMiles: boolean;
	[key: string]: boolean;
};

type SalesLogTableProps = {
	data: ISalesLog[];
	tableTitle: string;
	total: number;
};

const columns: GridColDef[] = [
	{
		field: "_id",
		headerName: "ID",
		filterable: false,
	},
	{
		field: "saleDate",
		headerName: "Sale Date",
		type: "date",
		width: 90,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		renderHeader: (params) => <TypographyHeader params={params} />,
		valueGetter: (value: string) => value && getDateInUTC(new Date(moment.utc(value).format(DATE_FORMAT.monthAbbrDate))),
		valueFormatter: (value: string) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
		renderCell: (params) => {
			if (isFooterCell(params)) {
				return <TypographyCell hints={{ isFooter: true, noWrap: true, noFocus: true }}>Total</TypographyCell>;
			}
			return <TypographyCell params={params} />;
		},
	},
	{
		field: "dealNumber",
		headerName: "Deal #",
		width: 70,
		minWidth: 70,
		maxWidth: 70,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params) => <TypographyCell params={params} />,
	},
	{
		field: "__empty",
		headerName: "",
		sortable: false,
		resizable: false,
		filterable: false,
		width: 12,
		maxWidth: 12,
		headerClassName: "!z-10 !bg-white !border-none !p-0",
		cellClassName: () => "empty-row !bg-white !border-none !p-0",
		renderCell: () => <TypographyCell className={"!bg-transparent"} />,
	},
	{
		field: "dealStatus",
		headerName: "Deal Status",
		width: 90,
		minWidth: 90,
		maxWidth: 90,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		filterOperators: [
			...getGridStringOperators(), // :contentReference[oaicite:0]{index=0}
			// then our custom “Not equal”
			notEqualsOperator,
		],
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params: GridRenderCellParams) => {
			if (!params.value) {
				return undefined;
			}
			return (
				<TypographyCell params={params}>
					<CircleRoundedIcon
						className={`deal-status-${params.value}`}
						sx={{
							fontSize: "12px",
							marginTop: "-3px",
							marginRight: "5px",
						}}
					/>
					{params.value}
				</TypographyCell>
			);
		},
	},
	{
		field: "customerName",
		headerName: "Customer Name",
		width: 140,
		minWidth: 140,
		resizable: true,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], { noFocus: true, noWrap: true }, {}),
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params: GridRenderCellParams) => <TypographyCell params={params} />,
	},
	{
		field: "financeEmployeeId",
		headerName: "Finance Employee Id",
	},
	{
		field: "financeManagerName",
		headerName: "Finance Manager",
		width: 130,
		minWidth: 130,
		resizable: true,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], { noFocus: true, noWrap: true }, {}),
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params) => <TypographyCell params={params} />,
	},
	{
		field: "salesEmployeeId",
		headerName: "Sales Employee Id",
	},
	{
		field: "salesEmployeeName",
		headerName: "Sales Associate",
		width: 130,
		minWidth: 130,
		resizable: true,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], { noFocus: true, noWrap: true }, {}),
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params: GridRenderCellParams) => {
			let value = params.row.salesEmployeeName || params.row.salesEmployeeId;
			if (params.row.salesEmployeeId === "9999") {
				value = "HOUSE";
			}
			return <TypographyCell params={params}>{value}</TypographyCell>;
		},
	},
	{
		field: "dealManagerName",
		headerName: "Sales Manager",
		width: 140,
		minWidth: 140,
		maxWidth: 140,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], { noFocus: true, noWrap: true }, {}),
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params) => <TypographyCell params={params} />,
	},
	{
		field: "stockNumber",
		headerName: "Stock #",
		width: 70,
		minWidth: 70,
		maxWidth: 70,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params) => <TypographyCell params={params} />,
	},
	{
		field: "age",
		headerName: "Age",
		width: 50,
		minWidth: 50,
		maxWidth: 50,
		align: "right",
		type: "number",
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], { noWrap: true, noFocus: true }, {}),
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params) => {
			if (isFooterCell(params)) {
				return <TypographyCell hints={{ isFooter: true, noWrap: true, noFocus: true }}>{Math.trunc(params.value)}</TypographyCell>;
			}

			return <TypographyCell params={params} />;
		},
	},
	{
		field: "description",
		headerName: "Description",
		resizable: true,
		width: 100,
		minWidth: 100,
		maxWidth: 300,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], { noFocus: true, noWrap: true }, { base: "min-w-[250px]" }),
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params) => <TypographyCell params={params} />,
	},
	{
		field: "saleType",
		headerName: "Sale Type",
		width: 50,
		minWidth: 50,
		maxWidth: 50,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], { noFocus: true }, {}),
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params) => <TypographyCell params={params} />,
	},
	{
		field: "dealType",
		headerName: "Deal Type",
		width: 65,
		minWidth: 65,
		maxWidth: 65,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], { noFocus: true }, {}),
		valueFormatter: (value: TDealTypeKey) => DEAL_TYPE_MAP?.[value] || value,
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params) => <TypographyCell params={params} />,
	},
	{
		field: "dealerFee",
		headerName: "Dealer Fee",
		type: "number",
		width: 110,
		minWidth: 110,
		maxWidth: 110,
		align: "right",
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params: GridRenderCellParams) => (
			<TypographyCell params={params} render={"absolute"} hints={{ isMoney: true, noFocus: true }} />
		),
	},
	{
		field: "units",
		headerName: "Units",
		type: "number",
		width: 70,
		minWidth: 70,
		maxWidth: 70,
		align: "right",
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], { noFocus: true }, {}),
		valueFormatter: (value: number) => accountFormat(value, 1),
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params: GridRenderCellParams) => <TypographyCell params={params} render={"absolute"} />,
	},
];

/***************************************** HELPERS ********************************************/
const findMaxKeys = (data: ISalesLog[]) => {
	let frontMax = 0;
	let backMax = 0;
	let frontKey = -1;
	let backKey = -1;

	data.forEach((element, key) => {
		if (element.frontProducts.length && element.frontProducts.length > frontMax) {
			frontMax = element.frontProducts.length;
			frontKey = key;
		}

		if (element.backProducts.length && element.backProducts.length > backMax) {
			backMax = element.backProducts.length;
			backKey = key;
		}
	});

	return { frontKey, backKey };
};

function addStaticColumns(currentColumns: GridColDef[]) {
	const staticColumns: GridColDef[] = [
		{
			field: "frontGrossProfit",
			headerName: "Total Front Gross",
			width: 110,
			minWidth: 110,
			align: "right",
			type: "number",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], { isMoney: true, noFocus: true }, {}),
			renderHeader: (params: GridColumnHeaderParams) => <TypographyHeader params={params} />,
			renderCell: (params: GridRenderCellParams) => <TypographyCell params={params} render={"absolute"} />,
		},
		{
			field: "backGrossProfit",
			headerName: "Total Back Gross",
			width: 110,
			minWidth: 110,
			align: "right",
			type: "number",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], { isMoney: true }, {}),
			renderHeader: (params: GridColumnHeaderParams) => <TypographyHeader params={params} />,
			renderCell: (params: GridRenderCellParams) => <TypographyCell params={params} render={"absolute"} />,
		},
		{
			field: "tradeACV",
			headerName: "Trade ACV",
			type: "number",
			width: 120,
			minWidth: 120,
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], { isMoney: true, noFocus: true }, {}),
			renderHeader: (params: GridColumnHeaderParams) => <TypographyHeader params={params} />,
			renderCell: (params: GridRenderCellParams) => {
				if (isFooterCell(params)) {
					return (
						<TypographyCell hints={{ isFooter: true, noWrap: true, noFocus: true }}>{`Trades: ${parseInt(
							params.value,
							10
						)}`}</TypographyCell>
					);
				}
				return <TypographyCell params={params} render={"absolute"} hints={{ isMoney: true }} />;
			},
		},
		{
			field: "tradeMiles",
			headerName: "Trade Miles",
			type: "number",
			width: 120,
			minWidth: 120,
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], { noFocus: true }, {}),
			renderHeader: (params: GridColumnHeaderParams) => <TypographyHeader params={params} />,
			valueFormatter: (value: number) => accountFormat(value, 0),
			renderCell: (params: GridRenderCellParams) => <TypographyCell params={params} />,
		},
	];

	return currentColumns.concat(staticColumns);
}

function buildColumns(items: ProductDetails[] = [], key = ""): GridColDef[] {
	return items.map((element) => {
		// Set custom header names for GPS products
		let headerName: string;
		if (element.product === "GPS") {
			headerName = key === "frontProducts" ? "GPS Pre-Load" : "GPS Extent";
		} else {
			headerName = element.product;
		}

		return {
			field: `${key}_${element.product}`,
			headerName,
			width: 110,
			hide: true,
			align: "right",
			type: "number",
			headerClassName: HEADER_CLASS_NAME_2,
			cellClassName: getCellClassNameFn([], { isMoney: true, noFocus: true }, {}),
			valueGetter: (_params, row) => {
				const product = (row[key] as ProductDetails[])?.filter((item) => item.product === element.product);
				return product?.[0]?.value ?? 0;
			},
			renderHeader: (params) => <TypographyHeader params={params} />,
			renderCell: (params: GridRenderCellParams) => <TypographyCell params={params} render={"absolute"} hints={{ isMoney: true }} />,
		};
	});
}

function buildProductColumns({ products, prefix, visibilityModel, footerModelsToShow, defaultColumnsToExpand }: ProductColumns) {
	products.forEach((element) => {
		visibilityModel[`${prefix}_${element.product}`] = false;
		footerModelsToShow[`${prefix}_${element.product}`] = "exclusiveSummation";
		defaultColumnsToExpand.push(`${prefix}_${element.product}`);
	});

	return buildColumns(products, prefix);
}
// Constant configurations
const pinnedColumns = {
	left: ["saleDate", "dealNumber", "dealStatus", "frontGrossProfit", "backGrossProfit", "__empty"],
};
export default function SalesLogTable(props: Readonly<SalesLogTableProps>) {
	const aggregations = useAggregationExtended({
		conditionalCount: {
			fieldsToFilter: [
				{ excludeField: "dealStatus", comparisonOperator: "ne", excludeValue: BACKOUT },
				{ excludeField: "tradeACV", comparisonOperator: "gt", excludeValue: 0 },
			],
		},
		exclusiveSummation: {
			fieldsToFilter: "dealStatus",
			exclusionValue: BACKOUT,
		},
		exclusiveAvg: {
			fieldsToFilter: [{ excludeField: "dealStatus", comparisonOperator: "ne", excludeValue: BACKOUT }],
		},
	});

	// Sales Log Context
	const { apiRef, onFilterModelChange, onDataChange } = useSalesLogChart();

	useEffect(() => {
		onDataChange(props.data);
	}, [props.data]);

	// Local State
	const [totalColumns, setTotalColumns] = useState<GridColDef[] | null>(null);
	const [footerModels, setFooterModels] = useState<DictionaryMap<string> | null>(null);
	const [showColumns, setShowColumns] = useState(false);
	const [columnsToExpand, setColumnsToExpand] = useState<string[]>([]);
	const [sx, setSx] = useState<SxProps>({});

	// Permissions
	const { useAuth } = useAuthorization();
	const viewSalesManager = useAuth(actions.VIEW_SALES_LOG_SALES_MANAGER);

	// State should control the visibility of the columns, the 'hide' prop will be deprecated in the future.
	const [columnVisibilityModel, setColumnVisibilityModel] = useState<Partial<{ [key in keyof ISalesLog]: boolean }>>({
		_id: false,
		salesEmployeeId: false,
		financeEmployeeId: false,
		tradeACV: false,
	});

	useEffect(() => {
		let currentColumns = [...columns];
		if (!viewSalesManager) {
			currentColumns = currentColumns.filter((column) => column.field !== "dealManagerName");
		}

		const defaultColumnsToExpand: string[] = [];
		const visibilityModel: VisibilityModel = {
			_id: false,
			salesEmployeeId: false,
			financeEmployeeId: false,
			tradeACV: false,
			tradeMiles: false,
		};

		const footerModelsToShow: DictionaryMap<string> = {
			age: "exclusiveAvg",
			backGrossProfit: "exclusiveSummation",
			dealerFee: "exclusiveSummation",
			frontGrossProfit: "exclusiveSummation",
			tradeMiles: "exclusiveSummation",
			units: "exclusiveSummation",
		};

		const { frontKey, backKey } = findMaxKeys(props.data);

		if (frontKey !== -1) {
			const frontProducts = buildProductColumns({
				products: props?.data[frontKey]?.frontProducts || [],
				prefix: "frontProducts",
				visibilityModel,
				footerModelsToShow,
				defaultColumnsToExpand,
			});
			currentColumns = currentColumns.concat(frontProducts);
		}

		if (backKey !== -1) {
			const backProducts = buildProductColumns({
				products: props?.data[backKey]?.backProducts || [],
				prefix: "backProducts",
				visibilityModel,
				footerModelsToShow,
				defaultColumnsToExpand,
			});
			currentColumns = currentColumns.concat(backProducts);
		}

		currentColumns = addStaticColumns(currentColumns);

		setColumnsToExpand([...defaultColumnsToExpand, "tradeACV", "tradeMiles"]);
		setColumnVisibilityModel(visibilityModel);
		setFooterModels(footerModelsToShow);
		setTotalColumns(currentColumns);

		return () => {
			setShowColumns(false);
			setTotalColumns(null);
			setFooterModels(null);
			setColumnsToExpand([]);
			setColumnVisibilityModel({
				_id: false,
				salesEmployeeId: false,
			});
		};
	}, [props, viewSalesManager, apiRef]);

	useEffect(() => {
		const api = apiRef.current;
		if (!api) return;

		const dims = api.state?.dimensions;
		if (!dims || !dims.viewportOuterSize || !dims.minimumSize) return;

		setSx(generateSx(apiRef, {}));

		return api.subscribeEvent("virtualScrollerContentSizeChange", () => {
			const d = apiRef.current?.state?.dimensions;
			if (!d || !d.viewportOuterSize || !d.minimumSize) return;
			setSx(generateSx(apiRef, {}));
		});
	}, [totalColumns, apiRef]);

	if (!totalColumns || !footerModels || (!props.data?.length && props.total === 0)) {
		return (
			<EmptyBody
				image={empty}
				title={"No Sales Found"}
				imageStyles={{ width: "330px", marginTop: "65px" }}
				bodyHeight={"320px"}
				sx={{ height: "500px !important", boxShadow: "none !important" }}
			/>
		);
	}

	const customProps: DataSmartTableProps["customProps"] = {
		tableContainerClassName: clsx({
			"[&_.MuiDataGrid-main]:mt-2 after:[&_.MuiDataGrid-topContainer]:!bg-disabled": true,
			"[&_.toolbar-actions]:!absolute [&_.toolbar-actions]:!right-[0px] [&_.toolbar-actions]:!top-[4px]": true,
		}),
		tableClassName: {
			"[&_.MuiDataGrid-columnHeader--withRightBorder]:!border-none": true,
			"[&_.MuiDataGrid-filler--pinnedLeft]:!border-r-transparent": true,
			"[&_.MuiDataGrid-row:not(.footer):hover_.MuiDataGrid-cell--pinnedLeft:not(.empty-row,.group-child)]:shadow-table-hov": true,
			"[&_.odd-row_.MuiDataGrid-cell--pinnedLeft:not(.empty-row)]:bg-[var(--gray-row-color)]": true,
			"[&_.even-row_.MuiDataGrid-cell--pinnedLeft:not(.empty-row)]:bg-[var(--white-color)]": true,
		},
		aggregationFooterClassName: "aggregation-footer regular-footer",
		expandableRows: false,
		hide: false,
		customAggregationsFn: { ...aggregations },
	};

	return (
		<Stack pt={3} pb={6} component={"section"}>
			<Typography className={"page-title"}>{props.tableTitle}</Typography>

			<DataSmartTable
				apiRef={apiRef}
				columns={totalColumns}
				pinnedColumns={pinnedColumns}
				rows={props?.data || []}
				maxRows={17}
				enableCustomScrollbars
				hideFooter
				disableColumnMenu
				disableRowSelectionOnClick
				disableMultipleRowSelection
				checkboxSelection={false}
				getRowClassName={getRowClassName}
				columnVisibilityModel={columnVisibilityModel as GridColumnVisibilityModel}
				filterStoreConfig={{
					key: tableKeys.DATA_PAGES.SALES_LOG,
				}}
				onFilterModelChange={onFilterModelChange}
				initialState={{
					aggregation: {
						model: { ...footerModels, tradeACV: "conditionalCount" },
					},
				}}
				slots={{
					toolbar: CommonToolbar,
					noRowsOverlay: NoRowsOverlay,
				}}
				throttleRowsMs={1200}
				slotProps={{
					toolbar: {
						filter: {
							hide: false,
						},
						search: {
							useQuickFilter: true,
						},
						expand: {
							setColumnVisibilityModel,
							hide: false,
							expanded: showColumns,
							columnsCollapsible: columnsToExpand,
							onExpandAction: (expanded: boolean) => {
								setShowColumns(expanded);
							},
						},
						customComponent: (
							<Stack direction="row" alignItems="center" sx={{ height: "auto" }}>
								<IncludeCheckBoxFilter />
							</Stack>
						),
					},
					noRowsOverlay: {
						title: "No Sales Log Data",
					},
				}}
				getCellClassName={(params: GridCellParams) => {
					// Targeting footer
					if (params.id === AUTO_GENERATED_ID) {
						// Use this to change the cell class name depending on the cell value
						const negatives = Object.keys(footerModels);
						if (negatives.includes(params.field) && (params.value as number) < 0) {
							return "aggregation-footer-negative";
						}

						// Append label 'Total' to the cell value in footer
						if (params.field === "saleDate") {
							return "aggregation-footer-total";
						}
					}

					return "";
				}}
				customProps={customProps}
				sx={sx}
			/>
		</Stack>
	);
}
